<script setup lang="ts">
import { HeatmapChart } from '@/components/charts/HeatmapChart'
import ApexChart from 'apexcharts'

import dayjs from 'dayjs'

import useSessionStorage from '@/composables/sessionStorage'
const chartInstance = ref(null)
const chart = new HeatmapChart()
const formattedData = ref([])
const { t } = useI18n()
const { dashboardItem } = useSessionStorage()
const { formatNumber } = useNumber()
const { tooltipAreaHeatmapContent } = useDashboard()

const noData = computed(() => {
  return !dashboardItem.value?.heatmap || formattedData.value[0]?.data?.length === 0
})

const data = computed(() => {
  return dashboardItem.value?.heatmap?.visitorsByHour
})

const chartRangeData = () => {
  const range = dashboardItem.value?.heatmap?.heatmapLegend
  return [
    {
      from: range[0],
      to: range[1],
      name: 'low',
      color: '#e5e5e5'
    },
    {
      from: range[1],
      to: range[2],
      name: 'medium',
      color: '#ddd6fe'
    },
    {
      from: range[2],
      to: range[3],
      name: 'high',
      color: '#a78bfa'
    },
    {
      from: range[3],
      to: range[4],
      name: 'extreme',
      color: '#5324bc'
    }
  ]
}

const initChart = () => {
  formatData()
  if (!noData.value) {
    const chartElement = createElement('hourlyVisits')
    chart.setColor(['#5b21b6'])
    chart.setSeries(formattedData.value)
    if (dashboardItem.value?.heatmap?.heatmapLegend) {
      chart.setPlotRange(chartRangeData())
    }
    setChartFormatters()
    chartInstance.value = new ApexChart(chartElement, chart.options)
    chartInstance.value.render()
  }
}

const setChartFormatters = () => {
  chart.options.tooltip.custom = ({ seriesIndex, dataPointIndex, w }) => {
    const serie = w.globals.initialSeries
    const data = serie[seriesIndex].data[dataPointIndex]
    const title = `${data.fullDay} ${serie[seriesIndex].name}`
    const chartType = t('pages.dashboard.dashboardPage.subtitles.visitors', data.y > 1 ? 2 : 1)
    return tooltipAreaHeatmapContent(title, formatNumber(data.y).toString(), chartType)
  }
}

const createElement = (ref) => {
  let elem = document.getElementById(ref)
  let chartElement = document.createElement('div')
  chartElement = elem.appendChild(chartElement)
  chartElement.setAttribute('id', `${ref}-apex`)
  return chartElement
}

const formatData = () => {
  if (!data.value) {
    return
  }

  const groupBy = (array, key) => {
    return array.reduce((rv, x) => {
      ;(rv[x[key]] = rv[x[key]] || []).push(x)
      return rv
    }, {})
  }
  const groupedCounts = groupBy(data.value, 'checkinHour')

  let mergedCounts = Object.values(groupedCounts).map((hour: any[]) => {
    return {
      name: hour[0]['checkinHour'],
      data: hour
        .reduce((acc, curr) => {
          const day = curr['dayOfTheWeek']
          let found = acc.find((e) => e['dayOfTheWeek'] === day)
          if (found) {
            found['count'] += curr['count']
          } else {
            acc.push(curr)
          }
          return acc
        }, [])
        .sort((a, b) => a['dayOfTheWeek'] - b['dayOfTheWeek'])
    }
  })

  mergedCounts = Object.values(mergedCounts).sort((a, b) => b['name'] - a['name'])

  mergedCounts.map((mergedCount) => {
    mergedCount.name = `${mergedCount.name}h`
    const data = mergedCount.data.map((el) => ({
      x: dayjs().day(el['dayOfTheWeek']).format('ddd'),
      y: Math.round(el['count']),
      fullDay: dayjs().day(el['dayOfTheWeek']).format('dddd')
    }))
    mergedCount.data = data
  })

  return (formattedData.value = mergedCounts)
}

onMounted(() => {
  initChart()
})

onBeforeUnmount(() => {
  if (chartInstance.value) {
    chartInstance.value.destroy()
  }
})
</script>
<template>
  <div id="hourlyVisits" class="w-full" />
  <TheNoResult
    v-if="noData"
    image="/images/no-results-analytics.png"
    :title="t('pages.review.reviewAnalyticsPage.chart.noResults.title')"
    :description="t('pages.review.reviewAnalyticsPage.chart.noResults.description')"
  />
</template>
